/* eslint-disable import/no-unused-modules */
import moment from 'moment';

const DEFAULT_LOCALE = 'en-US';
const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';
const NOT_AVAILABLE = '-';
const LONG_YEAR = 'YYYY';
const SHORT_YEAR = 'YY';
const LONG_MONTH = 'MM';
const SHORT_MONTH = 'M';
const LONG_DAY = 'DD';
const SHORT_DAY = 'D';
/**
 * Formats a date/time value according to the specified options, using moment.js for parsing and formatting.
 * @param {Date|string|number} value - The date/time value to format.
 * @param {Object} [options] - Optional formatting options.
 */
const DATE_TIME_FORMAT = [
    'DD/MM/YYYYTHH:mm:ss.SSSZ',
    'DD/MM/YYYYTHH:mm:ss.SSS',
    'DD/MM/YYYYTHH:mm:ss',
    'DD/MM/YYYYTHH:mm',
    'DD/MM/YYYYTHH',
    'DD/MM/YYYY HH:mm:ss.SSS',
    'DD/MM/YYYY HH:mm:ss',
    'DD/MM/YYYY HH:mm',
    'DD/MM/YYYY HH',
    'DD/MM/YYYY',
    'DD-MM-YYYYTHH:mm:ss.SSSZ',
    'DD-MM-YYYYTHH:mm:ss.SSS',
    'DD-MM-YYYYTHH:mm:ss',
    'DD-MM-YYYYTHH:mm',
    'DD-MM-YYYYTHH',
    'DD-MM-YYYY HH:mm:ss.SSS',
    'DD-MM-YYYY HH:mm:ss',
    'DD-MM-YYYY HH:mm',
    'DD-MM-YYYY HH',
    'DD-MM-YYYY',
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    'YYYY-MM-DDTHH:mm:ss.SSS',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DDTHH:mm',
    'YYYY-MM-DDTHH',
    'YYYY-MM-DD HH:mm:ss.SSS',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DD HH',
    'YYYY-MM-DD',
    'MM/DD/YYYYTHH:mm:ss.SSSZ',
    'MM/DD/YYYYTHH:mm:ss.SSS',
    'MM/DD/YYYYTHH:mm:ss',
    'MM/DD/YYYYTHH:mm',
    'MM/DD/YYYYTHH',
    'MM/DD/YYYY HH:mm:ss.SSS',
    'MM/DD/YYYY HH:mm:ss',
    'MM/DD/YYYY HH:mm',
    'MM/DD/YYYY HH',
    'MM/DD/YYYY',
    'DD.MM.YYYYTHH:mm:ss.SSSZ',
    'DD.MM.YYYYTHH:mm:ss.SSS',
    'DD.MM.YYYYTHH:mm:ss',
    'DD.MM.YYYYTHH:mm',
    'DD.MM.YYYYTHH',
    'DD.MM.YYYY HH:mm:ss.SSS',
    'DD.MM.YYYY HH:mm:ss',
    'DD.MM.YYYY HH:mm',
    'DD.MM.YYYY HH',
    'DD.MM.YYYY',
];

// Map Intl-style tokens to moment format strings. Honor `hour12` when provided.
const dateStyleToToken = {
    short: null,  // computed dynamically based on locale
    medium: 'L',
    long: 'LL',
    full: 'dddd, LL'
};

const timeStyles = {
    short: 'short',
    medium: 'medium',
    long: 'long',
    full: 'full',
    withMS: 'withMS',
    withZoneMS: 'withZoneMS'
};

const timeFormats24Hours = {
    short: 'HH:mm',
    medium: 'HH:mm:ss',
    long: 'HH:mm:ss Z',
    full: 'HH:mm:ss Z',
    withMS: 'HH:mm:ss.SSS',
    withZoneMS: 'HH:mm:ss.SSS Z'
};

const timeFormats12Hours = {
    short: 'LT',
    medium: 'LTS',
    long: 'LTS Z',
    full: 'LTS Z',
    withMS: 'hh:mm:ss.SSS A',
    withZoneMS: 'hh:mm:ss.SSS A Z'
};

export const TIME_ZONE = {
    // Undefined tells Intl.DateTimeFormat to use the browser/runtime's local timezone.
    LOCAL: undefined,
    UTC: 'UTC'
};

const resolveLocale = () => {
    if (navigator) {
        if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
            return navigator.languages[0];
        }
        if (navigator.language) {
            return navigator.language;
        }
    }
    const globalLocale = moment.locale();
    return globalLocale || DEFAULT_LOCALE;
};

const resolveTimeZone = (explicitTimeZone) => {
    if (explicitTimeZone) {
        return explicitTimeZone;
    }
    return TIME_ZONE.LOCAL;
};

const resolveHour12 = (explicitHour12) => {
    if (typeof explicitHour12 === 'boolean') {
        return explicitHour12;
    }
    return undefined;
};

/**
 * Converts a date string value (e.g. 31-07-2026) to a valid moment.js object.
 * @param {Date|string|number} value
 * @param {string} format - the expected format of the input value, e.g. 'DD-MM-YYYY'
 * @returns {moment.Moment|null} a valid moment.js object, or null if value is invalid/empty
*/
export const convertDateToValidDate = (value, format) => {
    return moment(value, format);
};

/**
 * IF toDateObject RETURNING INVALID DATE THEN CONVERT DATE USING convertDateToValidDate FUNCTION,
 * THEN PASS THAT DATE INTO toDateObject FUNCTION */
const toDateObject = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    // First try loose parsing
    const m = moment(value);
    if (m.isValid()) {
        return m.toDate();
    }
    // Try parsing against the known set of accepted formats
    const m2 = moment(value, DATE_TIME_FORMAT, true);
    if (m2.isValid()) {
        return m2.toDate();
    }
    // Couldn't parse the value
    return null;
};

// Helper to obtain a moment instance with locale and timezone applied.
const buildMoment = (date, timeZone) => {
    const locale = resolveLocale();
    const tz = resolveTimeZone(timeZone);
    let m;
    if (tz === TIME_ZONE.UTC) {
        m = moment.utc(date);
    }
    else {
        // Without moment-timezone we only support local runtime timezone.
        m = moment(date);
    }
    return m.locale(locale);
};


const computeShortDateToken = (locale) => {
    // Start from the locale's 'l' format (localized short date), then convert
    // 4-digit year -> 2-digit year. For English locales, Intl short omits
    // leading zeros for month/day, so convert 'MM'/'DD' -> 'M'/'D'. For many
    // other locales (e.g. de-DE) the leading zeros are kept.
    let localeData = moment.localeData(locale);
    if (!localeData && typeof locale === 'string' && locale.includes(NOT_AVAILABLE)) {
        const base = locale.split(NOT_AVAILABLE)[0];
        localeData = moment.localeData(base);
    }
    let token = localeData ? localeData.longDateFormat('l') : DEFAULT_DATE_FORMAT;
    token = token.replace(LONG_YEAR, SHORT_YEAR);
    if (/^en(?:-|$)/i.test(locale)) {
        token = token.replace(LONG_MONTH, SHORT_MONTH).replace(LONG_DAY, SHORT_DAY);
    }
    else {
        // Ensure two-digit day/month for locales where moment's short format
        // may use single-letter tokens (e.g. 'D.M.YYYY'). Convert single
        // 'D' -> 'DD' and 'M' -> 'MM' to match Intl 'short' behavior.
        token = token.replaceAll(/D(?!D)/g, LONG_DAY).replaceAll(/M(?!M)/g, LONG_MONTH);
    }
    return token;
};
const timeStyleToToken = (timeStyle, hour12) => {
    // Determine whether to use 12-hour or 24-hour formats.
    // Use explicit `hour12` when provided; otherwise detect from the resolved locale.
    let useHour12 = hour12;
    if (useHour12 === undefined) {
        const locale = resolveLocale();
        const dtf = new Intl.DateTimeFormat(locale, { hour: 'numeric' });
        const resolved = dtf.resolvedOptions();
        useHour12 = resolved.hour12 === true;
    }

    if (useHour12 === false) {
        const map24 = {
            [timeStyles.short]: timeFormats24Hours.short,
            [timeStyles.medium]: timeFormats24Hours.medium,
            [timeStyles.long]: timeFormats24Hours.long,
            [timeStyles.full]: timeFormats24Hours.full,
            [timeStyles.withMS]: timeFormats24Hours.withMS,
            [timeStyles.withZoneMS]: timeFormats24Hours.withZoneMS,
        };
        return map24[timeStyle] || timeFormats24Hours.long;
    }

    const map12 = {
        [timeStyles.short]: timeFormats12Hours.short,
        [timeStyles.medium]: timeFormats12Hours.medium,
        [timeStyles.long]: timeFormats12Hours.long,
        [timeStyles.full]: timeFormats12Hours.full,
        [timeStyles.withMS]: timeFormats12Hours.withMS,
        [timeStyles.withZoneMS]: timeFormats12Hours.withZoneMS
    };
    return map12[timeStyle] || timeFormats12Hours.long;
};

const hasTimeZoneDesignator = (timestamp) => /(?:Z|[+-]\d{2}:?\d{2})$/i.test(timestamp);

export const normalizeUtcTimestamp = (timestamp) => {
    if (typeof timestamp !== 'string' || !timestamp.includes('T') || hasTimeZoneDesignator(timestamp)) {
        return timestamp;
    }

    return `${timestamp}Z`;
};

/**
 * Formats a date-only value, e.g. "1/15/2026".
 * @param {Date|string|number} value
 * @param {{ dateStyle?: 'short'|'medium'|'long'|'full', timeZone?: string, dateFormat?: string }} [options]
*/
export const formatDate = (value, { dateStyle = timeStyles.medium, timeZone, dateFormat } = {}) => {
    const date = toDateObject(value);
    if (!date) {
        return NOT_AVAILABLE;
    }
    // If a custom format string is provided, honor it directly.
    if (dateFormat && typeof dateFormat === 'string') {
        return buildMoment(date, timeZone).format(dateFormat);
    }
    let token;
    if (dateStyle === timeStyles.short) {
        token = computeShortDateToken(resolveLocale());
    }
    else {
        token = dateStyleToToken[dateStyle];
    }
    return buildMoment(date, timeZone).format(token);
};

/**
 * Formats a time-only value, e.g. "10:30:45 AM".
 * @param {Date|string|number} value
 * @param {{ timeStyle?: 'short'|'medium'|'long'|'full'|'withMS', timeZone?: string, hour12?: boolean, timeFormat?: string }} [options]
*/
export const formatTime = (value, { timeStyle = timeStyles.medium, timeZone, hour12, timeFormat } = {}) => {
    const date = toDateObject(value);
    if (!date) {
        return NOT_AVAILABLE;
    }
    const token = timeStyleToToken(timeStyle, resolveHour12(hour12));
    // honor explicit time format string
    if (timeFormat && typeof timeFormat === 'string') {
        return buildMoment(date, timeZone).format(timeFormat);
    }
    return buildMoment(date, timeZone).format(token);
};

/**
 * Formats a combined date-time value, e.g. "1/15/2026, 10:30:45 AM".
 * @param {Date|string|number} value
 * @param {{ dateStyle?: 'short'|'medium'|'long'|'full', timeStyle?: 'short'|'medium'|'long'|'full'|'withMS', timeZone?: string, hour12?: boolean, dateFormat?: string, timeFormat?: string, dateTimeFormat?: string }} [options]
*/
export const formatDateTime = (value, { dateStyle = timeStyles.medium, timeStyle = timeStyles.medium, timeZone, hour12, dateFormat, timeFormat, dateTimeFormat } = {}) => {
    const date = toDateObject(value);
    if (!date) {
        return NOT_AVAILABLE;
    }
    // If a full custom date-time format is provided, use it.
    if (dateTimeFormat && typeof dateTimeFormat === 'string') {
        return buildMoment(date, timeZone).format(dateTimeFormat);
    }

    // Determine date token (or use custom `dateFormat`).
    let dateToken;
    if (dateFormat && typeof dateFormat === 'string') {
        dateToken = dateFormat;
    }
    else if (dateStyle === timeStyles.short) {
        dateToken = computeShortDateToken(resolveLocale());
    }
    else {
        dateToken = dateStyleToToken[dateStyle];
    }

    // Determine time token (or use custom `timeFormat`).
    const timeToken = timeFormat && typeof timeFormat === 'string'
        ? timeFormat
        : timeStyleToToken(timeStyle, resolveHour12(hour12));

    return buildMoment(date, timeZone).format(`${dateToken} ${timeToken}`);
};

/**
 * Formats a from/to date pair as a single locale-aware range string, e.g. "1/15/2026 - 1/20/2026".
 * Returns '' if either endpoint is missing, matching the legacy getDateRangeString contract.
 * @param {Date|string|number} from
 * @param {Date|string|number} to
 * @param {{ dateStyle?: 'short'|'medium'|'long'|'full', timeZone?: string }} [options]
*/
export const formatDateRange = (from, to, options = {}) => {
    if (!from || !to) {
        return '';
    }
    return `${formatDate(from, options)} - ${formatDate(to, options)}`;
};

/**
 * Converts a date value (interpreted as an instant in the browser/runtime's
 * local timezone, e.g. Date/ISO string/timestamp) to a moment.js object in UTC mode.
 * @param {Date|string|number} value
 * @returns {moment.Moment|null} a moment in UTC mode, or null if value is invalid/empty
*/
export const convertLocalToUtc = (value) => {
    const date = toDateObject(value);
    if (!date) {
        return null;
    }
    return moment(date).utc();
};

/**
 * Converts a UTC date value to a moment.js object in the browser/runtime's
 * local timezone, with the resolved runtime locale (navigator.languages/
 * navigator.language) applied so subsequent .format() calls on the result
 * use locale-aware tokens (e.g. 'LL').
 * @param {Date|string|number} value
 * @returns {moment.Moment|null} a locale-aware moment in local timezone, or null if value is invalid/empty
*/
export const convertUtcToLocale = (value) => {
    const date = toDateObject(value);
    if (!date) {
        return null;
    }
    return moment.utc(value).local().locale(resolveLocale());
};

/**
 * Converts a date value to a locale-aware string using custom format options.
 * @param {Date|string|number} date
 * @param {{ weekday?: 'long'|'short'|'narrow',
 *           day?: 'numeric'|'2-digit',
 *           month?: 'numeric'|'2-digit'|'long'|'short'|'narrow',
 *           year?: 'numeric'|'2-digit'
 * }} [formatOptions]
 * @returns {string} a locale-aware date string, or null if value is invalid/empty
*/
/**
 * USE THIS FUNCTION WHEN ONLY NEED TO RENDER LOCALE-AWARE DATE STRINGS
 * IF DATE IS INVALID OR EMPTY, IT FALLS BACK TO THE DEFAULT FORMAT USING formatDate()
 */
export const convertToCustomLocaleDate = (date, formatOptions = {}) => {
    const dateObj = new Date(date);
    if (dateObj) {
        const locale = resolveLocale();
        return dateObj.toLocaleDateString(locale, formatOptions);
    } else {
        return formatDate(date);
    }
};

/**
* Guesses the date format of the given date string by trying each format in DATE_TIME_FORMAT.
* @param {string} dateStr
* @returns {string} the first matching date format from DATE_TIME_FORMAT, or local date format if none match
*/
export const getDateFormat = (dateStr) => {
    // Loop through and return the first strictly matching format
    for (const format of DATE_TIME_FORMAT) {
        if (moment(dateStr, format, true).isValid()) {
            return format;
        }
    }
    return getDateFormat(formatDate(moment().format('L')));
};
